import { comfortableRangeFraction } from './constants.js'
import type { VocalRange } from './types.js'

/**
 * Shift a phrase by whole octaves so its centre lands in the middle 60 % of the singer's
 * calibrated range (§5.8).
 *
 * Whole octaves only: transposing by anything else would change the exercise. A phrase's centre
 * is the midpoint of its own lowest and highest note, which is what "where the phrase sits"
 * means to a singer looking at it.
 */
export const transposeIntoRange = (reference: number[], range: VocalRange): number[] => {
  if (reference.length === 0) {
    return reference
  }

  const margin = ((range.high - range.low) * (1 - comfortableRangeFraction)) / 2
  const bandLow = range.low + margin
  const bandHigh = range.high - margin
  const bandCentre = (bandLow + bandHigh) / 2

  const centre = (Math.min(...reference) + Math.max(...reference)) / 2

  /** How far outside the comfortable band the phrase's centre would sit after `octaves`. */
  const outside = (octaves: number) => {
    const shifted = centre + octaves * 12
    if (shifted < bandLow) {
      return bandLow - shifted
    }
    return shifted > bandHigh ? shifted - bandHigh : 0
  }

  /** The real-valued shift landing the centre on the band centre, rounded both ways. */
  const ideal = (bandCentre - centre) / 12
  const candidates = [Math.floor(ideal), Math.ceil(ideal)]

  const best = candidates.reduce((chosen, candidate) => {
    const chosenOutside = outside(chosen)
    const candidateOutside = outside(candidate)
    if (candidateOutside !== chosenOutside) {
      return candidateOutside < chosenOutside ? candidate : chosen
    }
    /** Equally far outside — prefer the one sitting nearer the middle of the band. */
    const chosenDistance = Math.abs(centre + chosen * 12 - bandCentre)
    const candidateDistance = Math.abs(centre + candidate * 12 - bandCentre)
    return candidateDistance < chosenDistance ? candidate : chosen
  })

  return best === 0 ? [...reference] : reference.map(midi => midi + best * 12)
}

export interface RangeFit {
  /** The phrase shifted by whole octaves */
  midis: number[]
  /** Every note lies inside `[range.low, range.high]`. False: the caller should refuse or split the phrase. */
  fits: boolean
  /** How many notes of `midis` lie outside the range; 0 when it fits */
  notesOutside: number
}

/**
 * Like `transposeIntoRange`, but for phrases that may be wider than an exercise phrase, such as whole melodies
 * (GH #2353, Bowie 2026-10-18):
 * 1. the centred shift is kept when every note lands inside the range;
 * 2. otherwise any whole-octave shift that puts every note inside wins, the one centred nearest the range;
 * 3. when none does (the phrase is wider than the range, or no octave lands it inside), `fits` is false and the
 *    phrase is placed by its centre, so the caller decides rather than presenting it as fitting.
 */
export const fitIntoRange = (reference: number[], range: VocalRange): RangeFit => {
  if (reference.length === 0) {
    return { midis: [], fits: true, notesOutside: 0 }
  }
  const lowest = Math.min(...reference)
  const highest = Math.max(...reference)
  const fitsAt = (octaves: number) => lowest + octaves * 12 >= range.low && highest + octaves * 12 <= range.high
  const isOutside = (midi: number) => midi < range.low || midi > range.high

  const centred = transposeIntoRange(reference, range)
  const centredOctaves = (centred[0] - reference[0]) / 12
  if (fitsAt(centredOctaves)) {
    return { midis: centred, fits: true, notesOutside: 0 }
  }

  /** The whole-octave shifts that keep every note inside, nearest the middle of the range first */
  const fewest = Math.ceil((range.low - lowest) / 12)
  const most = Math.floor((range.high - highest) / 12)
  const rangeCentre = (range.low + range.high) / 2
  const phraseCentre = (lowest + highest) / 2
  const fitting = Array.from({ length: Math.max(0, most - fewest + 1) }, (_, i) => fewest + i).sort(
    (a, b) => Math.abs(phraseCentre + a * 12 - rangeCentre) - Math.abs(phraseCentre + b * 12 - rangeCentre)
  )
  const [octaves] = fitting
  if (octaves === undefined) {
    return { midis: centred, fits: false, notesOutside: centred.filter(isOutside).length }
  }
  return { midis: reference.map(midi => midi + octaves * 12), fits: true, notesOutside: 0 }
}
